import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import api from "../../../services/apiClient";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import utils from "../../../utils/utils";
import {
  getActiveSiteLookup,
  getInternalActiveStaffLookup,
  siteToDropdownOptions,
  staffToDropdownOptions,
  staffMappedToAnySite,
} from "../../../services/lookupService";
import StudyAssignedStaffPanel from "../../common/StudyAssignedStaffPanel";
import "../../common/StudyAssignedStaffPanel/StudyAssignedStaffPanel.scss";
import { loadStudyForModal } from "./studyFormLoadService";
import { buildStudyActivityUpdatePayload } from "./studyActivityPayload";
import { isArchivedStudyReadOnly } from "./studyArchivedReadOnly";

const staffRawToDualList = (staffList) =>
  staffToDropdownOptions(staffList).map((opt) => ({
    ...opt,
    userName:
      opt.staff?.StaffName ||
      `${opt.staff?.FirstName || ""} ${opt.staff?.LastName || ""}`.trim(),
    emailId: opt.staff?.EmailId || "",
  }));

export default function StudyUsersEditModal({
  open,
  studyRow,
  userData,
  studyStatusOptions = [],
  studyColorOptions = [],
  onClose,
  onSaved,
  showAlert,
  showLoader,
  hideLoader,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(null);
  const [assignedStaff, setAssignedStaff] = useState([]);
  const [initialAssignedStaffIds, setInitialAssignedStaffIds] = useState([]);
  const [allStaffRaw, setAllStaffRaw] = useState([]);

  const studyLabel = String(
    studyRow?.StudyName || studyRow?.studyName || "study",
  ).trim();

  useEffect(() => {
    if (!open || !studyRow || !userData?.OrganizationId) {
      setFormData(null);
      setAssignedStaff([]);
      setInitialAssignedStaffIds([]);
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      showLoader("Loading study users...");
      try {
        const [sites, staffRaw] = await Promise.all([
          getActiveSiteLookup(userData, {}, { forceRefresh: true }),
          getInternalActiveStaffLookup(userData, {}, { forceRefresh: true }),
        ]);
        const siteOpts = siteToDropdownOptions(sites);
        const loaded = await loadStudyForModal({
          userData,
          row: studyRow,
          studyStatusOptions,
          studyColorOptions,
          siteOptions: siteOpts,
          staffRaw,
        });
        if (cancelled) return;
        setAllStaffRaw(staffRaw);
        setFormData(loaded.formData);
        setAssignedStaff(loaded.assignedStaff);
        setInitialAssignedStaffIds(loaded.initialAssignedStaffIds || []);
      } catch {
        if (!cancelled) {
          showAlert?.({
            title: "Study users",
            message: "Could not load assigned users for this study.",
            type: "danger",
          });
          onClose?.();
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          hideLoader();
        }
      }
    };

    load();
    return () => {
      cancelled = true;
      hideLoader();
    };
    // Parent passes inline callbacks; reload only when the study/modal opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, studyRow, userData?.OrganizationId, userData?.StaffId]);

  const selectedSiteIds = useMemo(
    () => (formData?.selectedSites || []).map((site) => site.value).filter(Boolean),
    [formData],
  );

  const siteFilteredStaff = useMemo(() => {
    if (!selectedSiteIds.length) return [];
    return allStaffRaw.filter((staff) =>
      staffMappedToAnySite(staff, selectedSiteIds),
    );
  }, [allStaffRaw, selectedSiteIds]);

  const staffList = useMemo(() => {
    const byId = new Map();
    siteFilteredStaff.forEach((staff) => {
      byId.set(String(staff.StaffId).toLowerCase(), staff);
    });
    assignedStaff.forEach((row) => {
      const match = allStaffRaw.find(
        (staff) =>
          String(staff.StaffId).toLowerCase() === String(row.value).toLowerCase(),
      );
      if (match) byId.set(String(match.StaffId).toLowerCase(), match);
    });
    return staffRawToDualList([...byId.values()]);
  }, [allStaffRaw, assignedStaff, siteFilteredStaff]);

  const assignedStaffIds = useMemo(
    () => assignedStaff.map((item) => item.value).filter(Boolean),
    [assignedStaff],
  );

  const piId = formData?.principalInvestigator?.value;

  const handleToggleStaff = useCallback(
    (staffRow, shouldAdd) => {
      const staffId = staffRow?.StaffId;
      if (!staffId) return;

      if (
        !shouldAdd &&
        piId &&
        String(piId).toLowerCase() === String(staffId).toLowerCase()
      ) {
        showAlert?.({
          title: "Study users",
          message: "Principal Investigator must stay assigned to the study.",
          type: "warning",
        });
        return;
      }

      if (shouldAdd) {
        const option = staffRawToDualList([staffRow])[0];
        setAssignedStaff((prev) => {
          if (
            prev.some(
              (item) =>
                String(item.value).toLowerCase() === String(staffId).toLowerCase(),
            )
          ) {
            return prev;
          }
          return [...prev, option];
        });
        return;
      }

      setAssignedStaff((prev) =>
        prev.filter(
          (item) =>
            String(item.value).toLowerCase() !== String(staffId).toLowerCase(),
        ),
      );
    },
    [piId, showAlert],
  );

  const handleSave = async () => {
    if (!formData || !userData) return;
    if (isArchivedStudyReadOnly(studyRow)) return;

    if (!assignedStaff.length) {
      showAlert?.({
        title: "Study users",
        message: "Assign at least one staff member.",
        type: "warning",
      });
      return;
    }

    if (
      piId &&
      !assignedStaff.some(
        (staff) => String(staff.value).toLowerCase() === String(piId).toLowerCase(),
      )
    ) {
      showAlert?.({
        title: "Study users",
        message: "Principal Investigator must be included in the assigned users.",
        type: "warning",
      });
      return;
    }

    const invalidAssigned = assignedStaff.filter((row) => {
      const staff = allStaffRaw.find(
        (item) =>
          String(item.StaffId).toLowerCase() === String(row.value).toLowerCase(),
      );
      return staff && !staffMappedToAnySite(staff, selectedSiteIds);
    });
    if (invalidAssigned.length) {
      showAlert?.({
        title: "Study users",
        message:
          "One or more assigned staff are not mapped to the selected study site(s).",
        type: "warning",
      });
      return;
    }

    setSaving(true);
    showLoader("Saving study users...");
    try {
      const payload = buildStudyActivityUpdatePayload({
        userData,
        formData,
        assignedStaff,
        initialAssignedStaffIds,
        staffList,
      });
      const response = await api.post(
        API_ENDPOINTS.STUDY.STUDY_ACTIVITY,
        payload,
      );
      if (utils.isApiSuccess(response)) {
        showAlert?.({
          title: "Study users",
          message: utils.getApiResponseMessage(
            response,
            "Study users updated successfully.",
          ),
          type: "success",
        });
        onSaved?.();
        return;
      }
      showAlert?.({
        title: "Study users",
        message: utils.getApiResponseMessage(
          response,
          "Unable to update study users. Please try again.",
        ),
        type: "danger",
      });
    } catch (err) {
      showAlert?.({
        title: "Study users",
        message:
          err?.response?.data?.message ||
          err?.message ||
          "Something went wrong while saving study users.",
        type: "danger",
      });
    } finally {
      setSaving(false);
      hideLoader();
    }
  };

  if (!open || !studyRow) return null;

  return createPortal(
    <div
      className="modal fade show d-block study-users-edit-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="study-users-edit-title"
    >
      <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" id="study-users-edit-title">
              Change users — {studyLabel}
            </h5>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={onClose}
              disabled={saving}
            />
          </div>
          <div className="modal-body">
            {loading || !formData ? (
              <div className="study-users-edit-modal__loading">
                Loading assigned users…
              </div>
            ) : (
              <StudyAssignedStaffPanel
                staffRows={siteFilteredStaff}
                selectedSites={formData.selectedSites || []}
                assignedStaffIds={assignedStaffIds}
                onToggleStaff={handleToggleStaff}
                hasSitesSelected={selectedSiteIds.length > 0}
              />
            )}
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={loading || saving || !formData}
            >
              {saving ? "Saving…" : "Save users"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
